import type { FeatureCollection } from 'geojson';
import type { GeoJSONSource } from 'maplibre-gl';
import { useEffect } from 'react';
import { useMap } from './MapView';

export interface LiveTripPath {
  tripId: string;
  color: string;
  /** Driven so far (solid). */
  driven: { lat: number; lon: number }[];
  /** Still to go (dashed). */
  remaining: { lat: number; lon: number }[];
  selected?: boolean;
}

/** Distinct colors for concurrent trips; a trip keeps its color for its whole run (hashed from the id). */
export const TRIP_COLORS = ['#2563EB', '#DB2777', '#D97706', '#7C3AED', '#0891B2', '#DC2626', '#059669', '#4F46E5', '#C026D3', '#EA580C'];

export function tripColor(tripId: string): string {
  let h = 0;
  for (let i = 0; i < tripId.length; i++) h = (h * 31 + tripId.charCodeAt(i)) >>> 0;
  return TRIP_COLORS[h % TRIP_COLORS.length]!;
}

const SRC = 'live-trips';

/**
 * Every running trip at once: the driven path solid and the remaining path dashed, each trip in its own color.
 * The selected trip is drawn thicker. `beforeId` keeps the lines under the vehicles.
 */
export function LiveTripsLayer({ trips, beforeId }: { trips: LiveTripPath[]; beforeId?: string }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined;
    const fc: FeatureCollection = { type: 'FeatureCollection', features: [] };
    for (const t of trips) {
      for (const [part, pts] of [['driven', t.driven], ['remaining', t.remaining]] as const) {
        if (pts.length < 2) continue;
        fc.features.push({
          type: 'Feature',
          properties: { tripId: t.tripId, color: t.color, dashed: part === 'remaining' ? 1 : 0, selected: t.selected ? 1 : 0 },
          geometry: { type: 'LineString', coordinates: pts.map((p) => [p.lon, p.lat]) },
        });
      }
    }
    const src = map.getSource(SRC) as GeoJSONSource | undefined;
    if (src) src.setData(fc);
    else map.addSource(SRC, { type: 'geojson', data: fc });

    if (!map.getLayer(`${SRC}-casing`)) {
      const width = (solid: number, sel: number) => ['case', ['==', ['get', 'selected'], 1], sel, solid] as const;
      map.addLayer(
        { id: `${SRC}-casing`, type: 'line', source: SRC, filter: ['==', ['get', 'dashed'], 0], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': width(7, 9) as never } },
        before,
      );
      map.addLayer(
        { id: `${SRC}-driven`, type: 'line', source: SRC, filter: ['==', ['get', 'dashed'], 0], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': ['get', 'color'], 'line-width': width(4, 6) as never } },
        before,
      );
      map.addLayer(
        { id: `${SRC}-remaining-casing`, type: 'line', source: SRC, filter: ['==', ['get', 'dashed'], 1], layout: { 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': width(5, 7) as never, 'line-opacity': 0.6 } },
        before,
      );
      map.addLayer(
        { id: `${SRC}-remaining`, type: 'line', source: SRC, filter: ['==', ['get', 'dashed'], 1], layout: { 'line-cap': 'butt', 'line-join': 'round' }, paint: { 'line-color': ['get', 'color'], 'line-width': width(3, 4.5) as never, 'line-dasharray': [2, 2] } },
        before,
      );
    }
  }, [map, trips, beforeId]);

  return null;
}
